import {
  GraphQLArgument,
  GraphQLEnumType,
  GraphQLInterfaceType,
  GraphQLNonNull,
  GraphQLNullableType,
  GraphQLObjectType,
  GraphQLScalarType,
  GraphQLType,
  GraphQLUnionType,
  OperationTypeNode,
  isEnumType,
  isInterfaceType,
  isObjectType,
  isScalarType,
  isUnionType,
} from 'graphql';
import * as jsoncParser from 'jsonc-parser';
import { camelCase, invert } from 'lodash';
import * as monacoEditor from 'monaco-editor';

import { SelectionPathNode } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { makeRangeFromOffset } from 'src/app/graph/explorerPage/helpers/monacoHelpers';
import { createUniqueName } from 'src/lib/createUniqueName';
import {
  GraphQLArgumentNode,
  RootTypeNames,
  unwrapNestedType,
  unwrapType,
} from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { CurrentDocsContext } from './getCurrentDocsContext';

export interface AddedArgument {
  argumentName: string;
  type: GraphQLType;
  variableName: string | null | undefined;
  initialValue?: unknown;
}

interface FieldNode {
  prefix: string;
  suffix: string;
  children: FieldNode[];
}

export function getArgument(
  nodeName: string,
  arg: GraphQLArgument,
  existingVariables: string[],
  shouldManageVariables: boolean,
) {
  const variableName = createUniqueName(
    arg.name === 'id'
      ? // special-case 'id' args to always be prefixed by the field name
        `${nodeName} ${arg.name}`
      : [arg.name, `${nodeName} ${arg.name}`],
    existingVariables,
    camelCase,
  );

  if (shouldManageVariables) {
    existingVariables.push(variableName);
  }

  return {
    argumentName: arg.name,
    variableName: shouldManageVariables ? variableName : null,
    type: arg.type,
  };
}
export function getArgumentDefinition(
  node: { args: GraphQLArgument[]; name: string },
  existingVariables: string[],
  shouldManageVariables: boolean,
) {
  const addedArguments = node.args
    .filter(
      (
        arg,
      ): arg is GraphQLArgument & {
        type: GraphQLNonNull<GraphQLNullableType>;
      } => arg.type instanceof GraphQLNonNull && arg.defaultValue === undefined,
    )
    .map((arg) =>
      getArgument(node.name, arg, existingVariables, shouldManageVariables),
    );

  const definition = addedArguments.length
    ? `(${addedArguments
        .map(
          (a) =>
            `${a.argumentName}: ${a.variableName ? `$${a.variableName}` : ''}`,
        )
        .join(', ')})`
    : '';

  return {
    addedArguments,
    definition,
  };
}

function nodeToSnippet(
  node: SelectionPathNode,
  prefix: string,
  suffix: string,
  indentLevel: number,
  existingVariables: string[],
  shouldManageVariables: boolean,
  addedArguments: Array<AddedArgument>,
  offset: number,
): {
  addedArguments: Array<AddedArgument>;
  prefix: string;
  suffix: string;
  cursorOffset: number;
} {
  const indent = '  '.repeat(indentLevel);
  if (
    node instanceof GraphQLObjectType ||
    node instanceof GraphQLInterfaceType
  ) {
    const snippetPrefix = `${indent}... on ${node.name} {\n`;
    return {
      addedArguments,
      prefix: `${snippetPrefix}${prefix}`,
      suffix: `${suffix}\n${indent}}`,
      cursorOffset: snippetPrefix.length + offset,
    };
  }
  const nodeType = unwrapNestedType(node.type);
  const hasSubselection =
    nodeType instanceof GraphQLObjectType ||
    nodeType instanceof GraphQLUnionType ||
    nodeType instanceof GraphQLInterfaceType;

  const argumentDefinition = getArgumentDefinition(
    node,
    existingVariables,
    shouldManageVariables,
  );

  const field = `${indent}${node.name}${argumentDefinition.definition}`;
  if (hasSubselection) {
    const snippetPrefix = `${field} {\n`;
    return {
      addedArguments: [...addedArguments, ...argumentDefinition.addedArguments],
      prefix: `${snippetPrefix}${prefix}`,
      suffix: `${suffix}\n${indent}}`,
      cursorOffset: snippetPrefix.length + offset,
    };
  }
  return {
    addedArguments: [...addedArguments, ...argumentDefinition.addedArguments],
    prefix: field,
    suffix: '',
    cursorOffset: field.length,
  };
}

/**
 * Print a subpath and all fields under a main root node
 */
function printSingleNodePath(
  path: SelectionPathNode[],
  indentLevel: number,
  existingVariables: string[],
  shouldManageVariables: boolean,
): {
  addedArguments: Array<AddedArgument>;
  prefix: string;
  suffix: string;
  cursorOffset: number;
} {
  if (!isMinLengthArray(1, path)) {
    return {
      addedArguments: [],
      prefix: '',
      suffix: '',
      cursorOffset: 0,
    };
  }

  const [pathNode, ...restOfPath] = path;
  const {
    addedArguments,
    prefix: printedPrefix,
    suffix: printedSuffix,
    cursorOffset: childCursorOffset,
  } = printSingleNodePath(
    restOfPath,
    indentLevel + 1,
    existingVariables,
    shouldManageVariables,
  );
  return nodeToSnippet(
    pathNode,
    printedPrefix,
    printedSuffix,
    indentLevel,
    existingVariables,
    shouldManageVariables,
    addedArguments,
    childCursorOffset,
  );
}

/**
 * Print all inner fields and leaves recursively under a main root node
 */
export function printInnerSnippetRecursive(
  fields: SelectionPathNode[],
  indentLevel: number,
  existingVariables: string[],
  shouldManageVariables: boolean,
): {
  addedArguments: Array<AddedArgument>;
  snippet: string;
} {
  const root: FieldNode = { prefix: '', suffix: '', children: [] };
  const fieldPathQueue = fields.map((field) => {
    return {
      node: field,
      parent: root,
    };
  });
  const existingFields = new Set<string>();
  let recursionDepth = 0;
  const addedArguments: Array<AddedArgument> = [];

  // Recursively BFS with a max depth of 6 to prevent infinite recursion
  while (fieldPathQueue.length > 0 && recursionDepth <= 6) {
    const currentLevelSize = fieldPathQueue.length;

    // Check all fields at current depth
    for (let i = 0; i < currentLevelSize; i++) {
      const currentPath = fieldPathQueue.shift();
      const currentField = currentPath?.node;
      if (currentField) {
        const {
          prefix,
          suffix,
          addedArguments: fieldArgs,
        } = nodeToSnippet(
          currentField,
          '',
          '',
          indentLevel + recursionDepth,
          existingVariables,
          shouldManageVariables,
          [],
          0,
        );
        const newNode: FieldNode = {
          prefix,
          suffix,
          children: [],
        };

        const fieldType = unwrapType(currentField);

        // Add scalars fields
        if (isScalarType(fieldType) || isEnumType(fieldType)) {
          currentPath.parent.children.push(newNode);
          addedArguments.push(...fieldArgs);
        }
        // Add and enqueue new objects
        else if (
          !existingFields.has(`${fieldType.name}.${currentField.name}`)
        ) {
          currentPath.parent.children.push(newNode);
          addedArguments.push(...fieldArgs);
          existingFields.add(`${fieldType.name}.${currentField.name}`);
          getAllFields(currentField).forEach((field) => {
            fieldPathQueue.push({ node: field, parent: newNode });
          });
          getAllTypes(currentField).forEach((field) => {
            fieldPathQueue.push({ node: field, parent: newNode });
          });
        }
      }
    }
    recursionDepth += 1;
  }
  return {
    addedArguments,
    snippet: constructRecursiveString(root, indentLevel),
  };
}

/**
 * Print inner fields and leaves under a main root node
 */
export function printInnerSnippet(
  fields: SelectionPathNode[],
  indentLevel: number,
  existingVariables: string[],
  shouldManageVariables: boolean,
): {
  addedArguments: Array<AddedArgument>;
  snippet: string;
} {
  const childIndent = '  '.repeat(indentLevel + 1);
  const allFields: string[] = [];
  const addedArguments: Array<AddedArgument> = [];

  fields.forEach((field) => {
    const {
      prefix,
      suffix,
      addedArguments: fieldArgs,
    } = nodeToSnippet(
      field,
      '',
      '',
      indentLevel,
      existingVariables,
      shouldManageVariables,
      [],
      0,
    );
    addedArguments.push(...fieldArgs);
    allFields.push(`${prefix}${suffix && childIndent + suffix}`);
  });

  return { addedArguments, snippet: allFields.join('\n') };
}

function constructRecursiveString(
  node: FieldNode,
  indentLevel: number,
): string {
  const children = node.children
    .map((child) => constructRecursiveString(child, indentLevel + 1))
    .join('\n');
  const nodeChildren =
    !children && node.suffix ? '  '.repeat(indentLevel) : children;
  return `${node.prefix}${nodeChildren}${node.suffix}`;
}

function pascalCase(text: string) {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

function getLastNonLeafNodeFromPath(
  path: MinLengthArray<1, SelectionPathNode>,
): SelectionPathNode {
  const lastNonLeafNode = path
    .slice()
    .reverse()
    .find((node) => {
      const nodeNestedType =
        node && 'type' in node && unwrapNestedType(node.type);
      return !(
        nodeNestedType instanceof GraphQLScalarType ||
        nodeNestedType instanceof GraphQLEnumType
      );
    });

  if (!lastNonLeafNode) {
    throw new Error(
      `No non-scalars in path (this should be impossible? the first element should be an operation Type)`,
    );
  }
  return lastNonLeafNode;
}

function getLastNodeWithArgumentsFromPath(
  path: MinLengthArray<1, SelectionPathNode>,
): SelectionPathNode | undefined {
  return path
    .slice()
    .reverse()
    .find((node) => 'args' in node && node.args.length > 0);
}

/**
 * Print a top level operation
 */
export function printOperation(
  path: SelectionPathNode[],
  rootTypeNames: RootTypeNames,
  variables: string[],
  shouldManageVariables: boolean,
  childrenArguments: Array<AddedArgument>,
): {
  addedArguments: Array<AddedArgument>;
  prefix: string;
  suffix: string;
  cursorOffset: number;
} {
  if (!isMinLengthArray(1, path)) {
    return {
      addedArguments: [],
      prefix: '',
      suffix: '',
      cursorOffset: 0,
    };
  }

  const [operationNode, ...fieldPath] = path;
  const operationType = invert(rootTypeNames)[
    operationNode.name
  ] as OperationTypeNode;
  const {
    addedArguments,
    prefix: printedChildPrefix,
    suffix: printedChildSuffix,
    cursorOffset: childCursorOffset,
  } = printSingleNodePath(fieldPath, 1, variables, shouldManageVariables);
  if (!operationType) {
    // Fragment
    const fragmentPrefix = `fragment ${operationNode.name}Fragment on ${operationNode.name} {\n`;
    return {
      addedArguments,
      prefix: `${fragmentPrefix}${printedChildPrefix}`,
      suffix: `${printedChildSuffix}\n}`,
      cursorOffset: fragmentPrefix.length + childCursorOffset,
    };
  }

  const args = [...addedArguments, ...childrenArguments];
  const argumentsWithVariables = args.filter((a) => a.variableName);

  const operationName = pascalCase(
    (operationType === 'mutation'
      ? getLastNodeWithArgumentsFromPath(path) ||
        getLastNonLeafNodeFromPath(path)
      : getLastNonLeafNodeFromPath(path)
    ).name,
  );
  const operationVariables = argumentsWithVariables.length
    ? `(${argumentsWithVariables
        .filter((a) => a.variableName)
        .map((a) => `$${a.variableName}: ${a.type}`)
        .join(', ')})`
    : '';

  const operationPrefix = `${operationType} ${operationName}${operationVariables} {\n`;
  return {
    addedArguments,
    prefix: `${operationPrefix}${printedChildPrefix}`,
    suffix: `${printedChildSuffix}\n}`,
    cursorOffset: operationPrefix.length + childCursorOffset,
  };
}

export function getVariableEdits(
  variableModel: monacoEditor.editor.ITextModel,
  addedArguments: Array<AddedArgument>,
): jsoncParser.Edit[][] {
  let currentValue = variableModel.getValue();
  const variables: unknown = jsoncParser.parse(currentValue) ?? {};
  return addedArguments
    .filter(
      (argument): argument is typeof argument & { variableName: string } =>
        !!argument.variableName &&
        typeof variables === 'object' &&
        !!variables &&
        !(argument.variableName in variables),
    )
    .map(({ variableName, initialValue = null }) => {
      const edits = jsoncParser.modify(
        currentValue,
        [variableName],
        initialValue,
        { formattingOptions: { insertSpaces: true, tabSize: 2 } },
      );
      currentValue = jsoncParser.applyEdits(currentValue, edits);
      return edits;
    });
}

export function getVariableDefinitionEdits(
  currentDocsContext: CurrentDocsContext,
  addedArguments: Array<AddedArgument>,
  model: monacoEditor.editor.ITextModel,
) {
  const argumentsWithVariables = addedArguments.filter((a) => a.variableName);
  if (
    currentDocsContext.variableWritePosition &&
    argumentsWithVariables.length
  ) {
    const documentText = model.getValue();
    const lastVariablePosition =
      currentDocsContext.variables[
        currentDocsContext.variables.length - 1
      ]?.range.getStartPosition();
    const variableWhitespace = `${
      // If there are 0 or 1 variables, add `,` between variables. Otherwise respect what the user has done.
      currentDocsContext.variables.length < 2 ? ',' : ''
    }${
      (lastVariablePosition &&
        documentText
          .substring(0, model.getOffsetAt(lastVariablePosition))
          .match(/,?\s*$/)?.[0]) ||
      ' '
    }`;

    return [
      {
        range: monacoEditor.Range.fromPositions(
          currentDocsContext.variableWritePosition,
          currentDocsContext.variableWritePosition,
        ),
        text: variableDefinitionWrapper(
          `${
            currentDocsContext.variables.length ? variableWhitespace : ''
          }${argumentsWithVariables
            .map((a) => `$${a.variableName}: ${a.type}`)
            .join(variableWhitespace)}`,
          currentDocsContext,
        ),
      },
    ];
  }
  return [];
}

export interface Insertion {
  type: 'insertDocNode';
  edits: monacoEditor.editor.IIdentifiedSingleEditOperation[];
  endCursorOffset: number;
  variableEdits: jsoncParser.Edit[][];
}

function variableDefinitionWrapper(
  variables: string,
  currentDocsContext: CurrentDocsContext,
) {
  if (currentDocsContext.definitionKind === 'ShortQuery') {
    return `query (${variables}) `;
  }
  if (!currentDocsContext.variableDefinitionRange) {
    return `(${variables})`;
  }
  return variables;
}

function argumentDefinitionWrapper(
  args: string,
  currentDocsContext: CurrentDocsContext,
) {
  if (!currentDocsContext.argumentsRange) {
    return `(${args})`;
  }
  return args;
}

/**
 * Given a path, get all current level scalars.
 */
export function getAllCurrentScalars(
  node: SelectionPathNode,
): SelectionPathNode[] {
  return getAllFields(node).filter((field) => {
    const fieldType = unwrapType(field);
    return isScalarType(fieldType) || isEnumType(fieldType);
  });
}

/**
 * Given a path, get any additional nodes that should be added at the end.
 */
export function getAllFields(node: SelectionPathNode): SelectionPathNode[] {
  const endType = unwrapType(node);
  if (!isObjectType(endType) && !isInterfaceType(endType)) {
    return [];
  }
  return Object.values(endType.getFields());
}

/**
 * Given a path, get any additional nodes that should be added at the end.
 */
export function getAllTypes(node: SelectionPathNode): SelectionPathNode[] {
  const endType = unwrapType(node);
  if (isUnionType(endType)) {
    return Object.values(endType.getTypes());
  }
  return [];
}

/**
 * Add the remaining tree from the docs path and our new node
 */
export function getInsertion({
  childrenSnippet,
  childrenArguments,
  fullPath,
  currentDocsContext,
  model,
  variableModel,
  rootTypeNames,
  existingVariables,
  shouldManageVariables,
}: {
  childrenSnippet: string;
  childrenArguments: Array<AddedArgument>;
  fullPath: SelectionPathNode[];
  currentDocsContext: CurrentDocsContext;
  model: monacoEditor.editor.ITextModel;
  variableModel: monacoEditor.editor.ITextModel | null | undefined;
  rootTypeNames: RootTypeNames;
  existingVariables: string[];
  shouldManageVariables: boolean;
}): Insertion {
  const isTopLevel =
    currentDocsContext.matchType === 'none' ||
    currentDocsContext.matchType === 'root';
  const {
    prefix: pathPrefix,
    suffix: pathSuffix,
    cursorOffset,
    addedArguments,
  } = isTopLevel
    ? printOperation(
        fullPath,
        rootTypeNames,
        existingVariables,
        shouldManageVariables,
        childrenArguments,
      )
    : printSingleNodePath(
        fullPath,
        currentDocsContext.indentLevel,
        existingVariables,
        shouldManageVariables,
      );

  const offset = model.getOffsetAt(currentDocsContext.writePosition);
  const prefixSpacing = offset > 0 && isTopLevel ? '\n\n' : '';
  const prefix = isTopLevel
    ? `${prefixSpacing}${pathPrefix}`
    : `\n${pathPrefix}`;
  const suffix = isTopLevel ? pathSuffix : `${pathSuffix}\n`;
  const documentText = model.getValue();
  const precedingWhitespaceLength = !isTopLevel
    ? documentText.substring(0, offset).match(/\s+$/)?.[0]?.length
    : 0;

  const args = [...addedArguments, ...childrenArguments];
  const variableEdits = getVariableDefinitionEdits(
    currentDocsContext,
    args,
    model,
  );

  const indent = '  '.repeat(Math.max(currentDocsContext.indentLevel - 1, 0));

  return {
    type: 'insertDocNode',
    edits: [
      {
        range: makeRangeFromOffset(
          offset - (precedingWhitespaceLength ?? 0),
          model,
          precedingWhitespaceLength,
        ),
        text: `${prefix}${childrenSnippet}${suffix}${indent}`,
      },
      ...variableEdits,
    ],
    endCursorOffset:
      offset +
      cursorOffset +
      childrenSnippet.length +
      (offset > 0 ? 1 : 0) +
      (prefixSpacing.length > 0 ? 1 : 0) +
      (variableEdits[0]?.text.length || 0) -
      (precedingWhitespaceLength ?? 0),
    variableEdits: variableModel ? getVariableEdits(variableModel, args) : [],
  };
}

/**
 * Add the argument to an existing field
 */
export function getArgumentsInsertion(
  currentDocsContext: CurrentDocsContext,
  node: GraphQLArgumentNode,
  model: monacoEditor.editor.ITextModel,
  variableModel: monacoEditor.editor.ITextModel | null | undefined,
  existingVariables: string[],
  fieldName: string,
  shouldManageVariables: boolean,
): Insertion {
  const offset = model.getOffsetAt(currentDocsContext.writePosition);

  const addedArguments = [
    {
      variableName: shouldManageVariables
        ? createUniqueName(
            node.name === 'id'
              ? // special-case 'id' args to always be prefixed by the field name
                `${fieldName} ${node.name}`
              : [node.name, `${fieldName} ${node.name}`],
            existingVariables,
            camelCase,
          )
        : null,
      type: node.type,
      argumentName: node.name,
    },
  ];

  const documentText = model.getValue();
  const lastArgumentPosition = Object.values(
    currentDocsContext.arguments,
  ).reduce((pos: monacoEditor.Position | null, arg) => {
    const startPosition = arg.range.getStartPosition();
    if (!pos || pos.isBefore(startPosition)) return startPosition;
    return pos;
  }, null);
  const argumentsWhitespace = `${
    // If there are 0 or 1 variables, add `,` between variables. Otherwise respect what the user has done.
    Object.keys(currentDocsContext.arguments).length < 2 ? ',' : ''
  }${
    (lastArgumentPosition &&
      documentText
        .substring(0, model.getOffsetAt(lastArgumentPosition))
        .match(/,?\s*$/)?.[0]) ||
    ' '
  }`;

  const variableEdit = getVariableDefinitionEdits(
    currentDocsContext,
    addedArguments,
    model,
  );

  const argumentEdit = currentDocsContext.argumentsWritePosition && {
    range: monacoEditor.Range.fromPositions(
      currentDocsContext.argumentsWritePosition,
      currentDocsContext.argumentsWritePosition,
    ),
    text: argumentDefinitionWrapper(
      `${
        Object.keys(currentDocsContext.arguments).length
          ? argumentsWhitespace
          : ''
      }${addedArguments
        .map(
          (a) =>
            `${a.argumentName}: ${a.variableName ? `$${a.variableName}` : ''}`,
        )
        .join(argumentsWhitespace)}`,
      currentDocsContext,
    ),
  };

  return {
    type: 'insertDocNode',
    edits: [...(argumentEdit ? [argumentEdit] : []), ...variableEdit],
    endCursorOffset:
      offset +
      (shouldManageVariables ? (variableEdit[0]?.text.length ?? 0) : -3) +
      (argumentEdit?.text.length ?? 0),
    variableEdits: variableModel
      ? getVariableEdits(variableModel, addedArguments)
      : [],
  };
}
